//* Importing React
import React, { useState, useMemo, useCallback } from "react";

//* Importing React Bootstrap components
import { Modal, Button, Table, Spinner, Alert } from "react-bootstrap";

//* Importing CSVStudent type
import { CSVStudent } from "../../../../hooks/CSV/students/useCSVStudentData";

//* Importando hooks personalizados
import { useStudents } from "../../../../hooks/students/useStudents";

//* Importando ícones do Lucide
import { CheckCircle, RefreshCw, AlertCircle, X } from "lucide-react";

type ErrorDetailsModalProps = {
  show: boolean;
  onHide: () => void;
  errorDetails: {
    student: CSVStudent;
    error: string;
    is_restore?: boolean;
    restored?: boolean;
    id: string;
  }[];
  token: string;
  onStudentRestored: (id: string) => void;
};

const ErrorDetailsModal: React.FC<ErrorDetailsModalProps> = ({
  show,
  onHide,
  errorDetails,
  token,
  onStudentRestored,
}) => {
  const { restoreByBatchStudent } = useStudents(token);
  const [restoringIds, setRestoringIds] = useState<string[]>([]);
  const [showLocalSuccessMessage, setShowLocalSuccessMessage] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  /**
   * Ordena os detalhes de erro para priorizar restaurados e is_restore.
   */
  const sortedErrorDetails = useMemo(() => {
    return [...errorDetails].sort((a, b) => {
      if (a.restored && !b.restored) return -1;
      if (!a.restored && b.restored) return 1;
      if (a.is_restore && !b.is_restore) return -1;
      if (!a.is_restore && b.is_restore) return 1;
      return 0;
    });
  }, [errorDetails]);

  /**
   * Função para restaurar um aluno.
   * @param id - ID do aluno a ser restaurado.
   */
  const handleRestore = useCallback(
    async (id: string) => {
      setRestoringIds((prev) => [...prev, id]);
      try {
        await restoreByBatchStudent(id);
        // Notifica o componente pai sobre a restauração
        onStudentRestored(id);
        setShowLocalSuccessMessage(true);
        setTimeout(() => setShowLocalSuccessMessage(false), 3000);
      } catch (error) {
        console.error("Erro ao restaurar aluno:", error);
        setLocalError("Erro ao restaurar aluno. Tente novamente.");
      } finally {
        setRestoringIds((prev) =>
          prev.filter((restoringId) => restoringId !== id)
        );
      }
    },
    [restoreByBatchStudent, onStudentRestored]
  );

  /**
   * Função para fechar a mensagem de erro local.
   */
  const handleCloseLocalError = () => {
    setLocalError(null);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="fade">
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <AlertCircle className="text-primary me-2" size={24} />
          Erros Detalhados
        </Modal.Title>
        <Button
          variant="link"
          className="p-0 ms-auto border-0 text-muted"
          onClick={onHide}
        >
          <X size={24} />
        </Button>
      </Modal.Header>

      {/* Mensagem de Sucesso Local */}
      {showLocalSuccessMessage && (
        <Alert
          variant="success"
          className="mx-3 mt-3 mb-0 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(25, 135, 84, 0.1)",
            border: "none",
            borderRadius: "8px",
            padding: "0.75rem",
          }}
        >
          <CheckCircle size={20} className="me-2 text-success" />
        </Alert>
      )}

      {/* Mensagem de Erro Local */}
      {localError && (
        <Alert
          variant="danger"
          className="mx-3 mt-3 mb-0 d-flex align-items-center justify-content-center"
          onClose={handleCloseLocalError}
          dismissible
          style={{
            backgroundColor: "rgba(220, 53, 69, 0.1)",
            border: "none",
            borderRadius: "8px",
            padding: "0.75rem",
          }}
        >
          <AlertCircle size={20} className="me-2 text-danger" />
          <span className="text-danger">{localError}</span>
        </Alert>
      )}

      <Modal.Body className="px-4 py-3">
        {sortedErrorDetails.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <AlertCircle size={48} className="mb-3 opacity-50" />
            <p className="mb-0">Nenhum erro encontrado.</p>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
            className="table-responsive"
          >
            <Table hover className="mb-0">
              <thead>
                <tr className="bg-light">
                  <th className="border-bottom px-3 py-3">Nome</th>
                  <th className="border-bottom px-3 py-3">E-mail</th>
                  <th className="border-bottom px-3 py-3">Erro</th>
                  <th
                    className="border-bottom px-3 py-3 text-center"
                    style={{ width: "180px" }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedErrorDetails.map((error) => {
                  const isRestoring = restoringIds.includes(error.id);
                  const isRestored = error.restored;

                  return (
                    <tr
                      key={error.id}
                      className={`${
                        isRestored ? "bg-success bg-opacity-10" : ""
                      }`}
                    >
                      <td className="px-3 py-3">{error.student.name}</td>
                      <td className="px-3 py-3">{error.student.email}</td>
                      <td className="px-3 py-3">
                        <span className="text-danger d-flex align-items-center">
                          <AlertCircle
                            size={16}
                            className="me-2 flex-shrink-0"
                          />
                          {error.error}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {isRestored ? (
                          <span className="text-success d-flex align-items-center justify-content-center">
                            <CheckCircle size={18} className="me-2" />
                            Restaurado
                          </span>
                        ) : error.is_restore ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={isRestoring}
                            onClick={() => handleRestore(error.id)}
                            className="d-inline-flex align-items-center"
                            style={{
                              minWidth: "120px",
                              transition: "all 0.2s",
                            }}
                          >
                            {isRestoring ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Restaurando
                              </>
                            ) : (
                              <>
                                <RefreshCw size={16} className="me-2" />
                                Restaurar
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted d-flex align-items-center justify-content-center">
                            <X size={16} className="me-1" />
                            Não disponível
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        <Button
          variant="secondary"
          onClick={onHide}
          className="px-4"
          style={{
            backgroundColor: "#6c757d",
            borderRadius: "6px",
          }}
        >
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ErrorDetailsModal;
